export const airconHero = {
  badge: "Annual Research Gathering",
  title: "Annual International Research Conference",
  subtitle:
    "Held every January, AIRCon convenes researchers, factory leaders, brand sustainability heads, and policymakers for two days of paper presentations, panel debates, and factory field visits. It sets the annual research agenda for Bangladesh's textile sector.",
  ctaPrimary: "Register for AIRCon",
  ctaSecondary: "View Past Proceedings",
};

export const airconWhatIs = {
  title: "What is AIRCon?",
  paragraphs: [
    "AIRCon - the Annual International Research Conference - is TIE's flagship knowledge dissemination event. Every January, it brings together the full ecosystem of textile innovation stakeholders for two intensive days of learning, debate, and collaboration.",
    "The conference is deliberately designed to bridge the gap between research and practice. Academic researchers present validated findings that have been tested on factory floors. Factory leaders share implementation challenges and real-world outcomes. Brand representatives discuss what they need from suppliers. Policymakers outline regulatory directions. The result is a shared research agenda that reflects what the sector actually needs.",
    "AIRCon is more than a conference — it is an annual calibration of the sector's innovation priorities. The proceedings volume documents every paper and panel, creating a permanent record of the sector's intellectual progress.",
  ],
  highlights: [
    { label: "Held", value: "January" },
    { label: "Duration", value: "2 Days" },
    { label: "Delegates", value: "400+" },
    { label: "Papers", value: "40+" },
  ],
};

export const airconConferenceTracks = [
  {
    name: "Process Innovation & Optimization",
    icon: "ri-settings-3-line",
    format: "Paper presentations + panel debate",
    focus: [
      "Dyeing and finishing efficiency breakthroughs",
      "Sewing line balancing and automation",
      "Quality stability and RFT improvement",
      "Energy and water reduction protocols",
    ],
  },
  {
    name: "Sustainability & Circularity",
    icon: "ri-recycle-line",
    format: "Case study showcases + roundtable",
    focus: [
      "Carbon footprint reduction pathways",
      "Circular fashion model implementation",
      "Chemical substitution and ZDHC alignment",
      "Waste valorization and recycling systems",
    ],
  },
  {
    name: "Digital Transformation",
    icon: "ri-cpu-line",
    format: "Technology demos + expert panels",
    focus: [
      "AI and machine vision in quality control",
      "IoT-based production monitoring",
      "Digital twin applications in textiles",
      "ERP and MES integration best practices",
    ],
  },
  {
    name: "Material Innovation",
    icon: "ri-stack-line",
    format: "Research presentations + lab tours",
    focus: [
      "MMF and technical textile development",
      "Natural fiber performance enhancement",
      "Biodegradable and compostable materials",
      "Smart textile and wearable technology",
    ],
  },
  {
    name: "Policy, Trade & Competitiveness",
    icon: "ri-government-line",
    format: "Keynote + ministerial panel",
    focus: [
      "Trade policy and GSP implications",
      "Labor law and compliance evolution",
      "Industrial policy and innovation incentives",
      "Bangladesh's competitiveness roadmap",
    ],
  },
  {
    name: "Human Capital & Skills",
    icon: "ri-team-line",
    format: "Workshops + certification ceremonies",
    focus: [
      "Training programme effectiveness data",
      "Multi-skill operator performance studies",
      "Leadership development for innovation",
      "Talent retention in T&A manufacturing",
    ],
  },
];

export const airconProcessSteps = [
  {
    phase: "Phase 1",
    week: "June–August",
    title: "Call for Papers & Abstract Submission",
    description:
      "The conference committee issues a call for papers aligned with the year's priority themes. Authors submit 300-word abstracts for review. PIARC circles, IJTAI authors, and independent researchers are all encouraged to submit.",
    activities: [
      "Call for papers announcement and theme publication",
      "Abstract submission via online portal",
      "Committee review and acceptance notification",
      "Full paper deadline and author guidelines shared",
    ],
  },
  {
    phase: "Phase 2",
    week: "September–November",
    title: "Full Paper Review & Programme Build",
    description:
      "Accepted authors submit full papers for peer review. The programme committee builds the conference schedule, assigns tracks, invites keynote speakers, and organizes factory field visits.",
    activities: [
      "Full paper peer review and feedback",
      "Conference programme and track scheduling",
      "Keynote speaker and panelist invitation",
      "Factory field visit route planning",
    ],
  },
  {
    phase: "Phase 3",
    week: "December–January",
    title: "Registration, Production & Event Execution",
    description:
      "Delegate registration opens. Proceedings volume enters production. The two-day conference runs with parallel tracks, plenary sessions, exhibition booths, networking dinners, and factory visits.",
    activities: [
      "Delegate registration and logistics coordination",
      "Proceedings volume design and printing",
      "Two-day conference execution with parallel tracks",
      "Networking events and factory field visits",
    ],
  },
  {
    phase: "Phase 4",
    week: "February–March",
    title: "Proceedings Publication & Research Agenda",
    description:
      "The proceedings volume is published and distributed. The committee synthesizes conference insights into an annual research priority framework that guides PIARC, IJTAI, and TIE programme design for the coming year.",
    activities: [
      "Proceedings volume publication and distribution",
      "Annual research priority framework synthesis",
      "Media coverage and sector communication",
      "Feedback collection for next year's improvement",
    ],
  },
];

export const airconOutcomes = {
  title: "What AIRCon Produces Every Year",
  items: [
    {
      title: "Annual Research Priority Framework",
      description:
        "A published document identifying the top 10 research priorities for Bangladesh's textile sector, guiding funders, institutions, and PIARC circles for the year ahead.",
      icon: "ri-map-2-line",
    },
    {
      title: "Published Proceedings Volume",
      description:
        "A permanent, citable record of all papers, panels, and keynotes from the conference — distributed to universities, factories, and government agencies.",
      icon: "ri-book-2-line",
    },
    {
      title: "Cross-Border Collaboration Network",
      description:
        "International delegates from India, China, Europe, and North America create research partnerships that extend Bangladesh's innovation reach globally.",
      icon: "ri-global-line",
    },
    {
      title: "Factory Field Visit Insights",
      description:
        "Live factory tours expose researchers and international delegates to real production conditions, grounding academic ideas in factory-floor reality.",
      icon: "ri-building-2-line",
    },
    {
      title: "Brand-Supplier Dialogue Platform",
      description:
        "Brand sustainability heads and factory leaders engage in structured dialogue about what buyers need, what factories can deliver, and where research should focus.",
      icon: "ri-exchange-line",
    },
    {
      title: "Talent & Career Visibility",
      description:
        "Young researchers and factory engineers gain stage time, network with sector leaders, and attract career opportunities through their conference presentations.",
      icon: "ri-user-star-line",
    },
  ],
};

export const airconStats = [
  { value: "400+", label: "Annual Delegates", sub: "researchers, leaders, brands" },
  { value: "40+", label: "Papers Presented", sub: "across 6 parallel tracks" },
  { value: "2", label: "Days", sub: "of intensive knowledge exchange" },
  { value: "15+", label: "Countries", sub: "represented by international delegates" },
];